import { z } from "zod";
import { type EscrowStatus, EscrowStatusSchema } from "@remi/enums/escrow-status";
import { EscrowVendorSchema } from "@remi/enums/escrow-vendor";
import type { FundingMilestone } from "@remi/enums/funding-milestone";
import { type FundingMilestoneSchedule, FundingMilestoneScheduleSchema } from "@remi/enums/funding-milestone-schedule";

import type { PhoneType } from "./phoneType";

export type EscrowUser = {
  email: string;
  firstName: string;
  lastName: string;
};

export type UpsertEscrowInput = {
  bid: { id: number };
  externalId?: string;
  propertyAddress: { addressStreet: string; city: string; stateCode: string; zip: string };
  homeownerInfo: {
    firstName: string;
    lastName: string;
    phone: string | undefined;
    phoneType: PhoneType | undefined;
    primaryEmailAddress: {
      emailAddress: string;
    } | null;
  };
  fundingMilestoneSchedule: FundingMilestoneSchedule;
  fundingInfo: {
    contractAmount: number;
  };
};

type UpsertEscrowReturn = {
  externalId: string;
  customerInviteLink: string | null;
};

export type UpsertEscrowFn = ({
  input,
  user,
  currFundingMilestoneSchedule,
}: {
  input: UpsertEscrowInput;
  user: EscrowUser;
  currFundingMilestoneSchedule: FundingMilestoneSchedule | null;
}) => Promise<UpsertEscrowReturn>;

type GetEscrowInput = {
  externalId: string;
};

export interface GetEscrowReturn {
  contractAmount: number;
  fundedAmount: number;
  pendingAmount: number;
  disbursedAmount: number;
  fullyFundedDate?: Date;
  fullyDisbursedDate?: Date;
  status: EscrowStatus;
  fundingMilestoneSchedule?: FundingMilestoneSchedule;
}

export type EscrowGetFn = ({ input }: { input: GetEscrowInput }) => Promise<GetEscrowReturn>;

type ClaimEscrowMilestoneInput = {
  externalId: string;
  fundingMilestone: FundingMilestone;
  fundingMilestoneSchedule: FundingMilestoneSchedule;
  files: { id: string; name: string }[];
  user: EscrowUser;
};

type ClaimEscrowMilestoneReturn = {
  success: boolean;
};

export type EscrowClaimMilestoneFn = (input: ClaimEscrowMilestoneInput) => Promise<ClaimEscrowMilestoneReturn>;

type ResendInput = {
  externalId: string;
  user: EscrowUser;
};

type ResendReturn = {
  success: boolean;
};

export type EscrowResendFn = (input: ResendInput) => Promise<ResendReturn>;

type CancelInput = {
  externalId: string;
  requestForQuoteCancelled: boolean;
  cancellationFee?: number;
  cancellationReasonText?: string;
};

type CancelReturn = {
  success: boolean;
};

export type EscrowCancelFn = (input: CancelInput) => Promise<CancelReturn>;

export interface EscrowPackage {
  upsert: UpsertEscrowFn;
  get: EscrowGetFn;
  claimMilestone: EscrowClaimMilestoneFn;
  resend: EscrowResendFn;
  cancel: EscrowCancelFn;
}

export const EscrowSchema = z.object({
  vendor: EscrowVendorSchema,
  status: EscrowStatusSchema,
  contractAmount: z.number(),
  fundedAmount: z.number(),
  pendingAmount: z.number(),
  fundingMilestoneSchedule: FundingMilestoneScheduleSchema.nullable(),
});
